import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { isAdjustmentPDF } from 'components/pages/finance/NewInvoice/services/service';

import { InvoiceHeader } from './InvoiceHeader/InvoiceHeader';
import { InvoiceItemsList } from './InvoiceItemsList/InviceItemsList';
import { styles } from './EncounterInvoiceTemplateStyles';
import { paginateInvoice } from './invoicePagination';

export const InvoicePageGenerator = ({
  normalizedGroups,
  setTotalPages,
  study,
  pcn,
  investigator,
  site,
  siteNumber,
  invoiceNumber,
  invoiceDate,
  terms,
  payee,
  remit,
  contact,
  billTo,
  invoiceType
}) => {
  const measureRowsRef = useRef(null);
  const contHeaderMeasureRef = useRef(null);
  const probePageBodyRef = useRef(null);
  const continuationProbeBodyRef = useRef(null);
  const bankInfoRef = useRef(null);
  const [layout, setLayout] = useState(null);

  const contHeaderGroups = useMemo(
    () => normalizedGroups.filter(g => g?.typeHeader).map(g => ({ ...g, groupHeaderSuffix: '(cont.)' })),
    [normalizedGroups]
  );

  useLayoutEffect(() => {
    setLayout(null);
  }, [
    normalizedGroups,
    invoiceType,
    study,
    pcn,
    investigator,
    site,
    siteNumber,
    invoiceNumber,
    invoiceDate,
    terms,
    payee,
    remit,
    contact,
    billTo
  ]);

  useLayoutEffect(() => {
    if (layout !== null) return;
    if (
      !measureRowsRef.current ||
      !contHeaderMeasureRef.current ||
      !probePageBodyRef.current ||
      !continuationProbeBodyRef.current
    ) {
      return;
    }

    const bodyHeight = probePageBodyRef.current.clientHeight;
    const continuationBodyHeight = continuationProbeBodyRef.current.clientHeight;
    const rowNodes = measureRowsRef.current.querySelectorAll('.row-item');
    const rowHeights = Array.from(rowNodes, node => node.offsetHeight);
    const bankInfoHeight = !isAdjustmentPDF(invoiceType) && bankInfoRef.current ? bankInfoRef.current.offsetHeight : 0;

    const contHeaderNodes = contHeaderMeasureRef.current.querySelectorAll('.row-item');
    const headerHeightByGroup = new Map();
    contHeaderGroups.forEach((group, i) => {
      headerHeightByGroup.set(group.groupName, contHeaderNodes[i]?.offsetHeight ?? 0);
    });
    const subHeaderHeightByGroup = new Map();
    normalizedGroups.forEach((row, i) => {
      if (row?.typeSubHeader) subHeaderHeightByGroup.set(row.subGroupName, rowHeights[i]);
    });

    const headerIdx = normalizedGroups.findIndex(g => g?.typeHeader);
    const subHeaderIdx = normalizedGroups.findIndex(g => g?.typeSubHeader);
    const headerHeight = headerIdx >= 0 ? rowHeights[headerIdx] : 0;
    const subHeaderHeight = subHeaderIdx >= 0 ? rowHeights[subHeaderIdx] : 0;

    setLayout({
      bodyHeight,
      continuationBodyHeight,
      rowHeights,
      bankInfoHeight,
      headerHeightByGroup,
      subHeaderHeightByGroup,
      representativeHeights: { headerHeight, subHeaderHeight }
    });
  }, [layout, normalizedGroups, invoiceType, contHeaderGroups]);

  const pages = useMemo(() => {
    if (!layout) return null;
    return paginateInvoice({
      normalizedGroups,
      rowHeights: layout.rowHeights,
      bodyHeight: layout.bodyHeight,
      continuationBodyHeight: layout.continuationBodyHeight,
      bankInfoHeight: layout.bankInfoHeight,
      headerHeightByGroup: layout.headerHeightByGroup,
      subHeaderHeightByGroup: layout.subHeaderHeightByGroup,
      representativeHeights: layout.representativeHeights,
      invoiceType
    });
  }, [layout, normalizedGroups, invoiceType]);

  useEffect(() => {
    if (pages && typeof setTotalPages === 'function') {
      setTotalPages(pages.length);
    }
  }, [pages, setTotalPages]);

  const totalPagesComputed = pages ? pages.length : 0;

  return (
    <>
      {!layout && (
        <>
          <div style={styles.hiddenPage} className="invoice-generator-hidden" aria-hidden>
            <div style={styles.section} ref={measureRowsRef}>
              <InvoiceItemsList items={normalizedGroups} invoiceType={invoiceType} />
            </div>
            <div style={styles.section} ref={contHeaderMeasureRef}>
              <InvoiceItemsList items={contHeaderGroups} invoiceType={invoiceType} />
            </div>
          </div>
          <div style={styles.page} className="invoice-generator-probe" aria-hidden data-testid="invoice-pdf-probe-page">
            <InvoiceHeader
              study={study}
              pcn={pcn}
              investigator={investigator?.split(', ').join(' ')}
              site={site}
              siteNumber={siteNumber}
              invoiceNumber={invoiceNumber}
              invoiceDate={invoiceDate}
              terms={terms}
              payee={payee}
              contact={contact}
              remit={remit}
              pageNumber={1}
              billTo={billTo}
              lastPageWithoutTable={false}
              invoiceType={invoiceType}
            />
            <div style={styles.section} ref={probePageBodyRef} />
            <div style={styles.pageCountWrapper}>
              <div style={styles.pageCountWrapper.pageCount}>1 of 1</div>
            </div>
          </div>
          <div
            style={styles.page}
            className="invoice-generator-probe"
            aria-hidden
            data-testid="invoice-pdf-continuation-probe-page"
          >
            <InvoiceHeader
              study={study}
              pcn={pcn}
              investigator={investigator?.split(', ').join(' ')}
              site={site}
              siteNumber={siteNumber}
              invoiceNumber={invoiceNumber}
              invoiceDate={invoiceDate}
              terms={terms}
              payee={payee}
              contact={contact}
              remit={remit}
              pageNumber={2}
              billTo={billTo}
              lastPageWithoutTable={false}
              invoiceType={invoiceType}
            />
            <div style={styles.section} ref={continuationProbeBodyRef} />
            <div style={styles.pageCountWrapper}>
              <div style={styles.pageCountWrapper.pageCount}>2 of 2</div>
            </div>
          </div>
        </>
      )}

      {pages &&
        pages.map((items, idx) => {
          const currentPageNumber = idx + 1;
          const isLastPage = currentPageNumber === totalPagesComputed;
          return (
            <div key={idx} style={styles.page} data-testid="invoice-pdf-page">
              <InvoiceHeader
                study={study}
                pcn={pcn}
                investigator={investigator?.split(', ').join(' ')}
                site={site}
                siteNumber={siteNumber}
                invoiceNumber={invoiceNumber}
                invoiceDate={invoiceDate}
                terms={terms}
                payee={payee}
                contact={contact}
                remit={remit}
                pageNumber={currentPageNumber}
                billTo={billTo}
                lastPageWithoutTable={items.length === 1}
                invoiceType={invoiceType}
              />
              <div style={styles.section}>
                <InvoiceItemsList items={items} invoiceType={invoiceType} />
              </div>
              <div style={styles.pageCountWrapper}>
                {isLastPage && !isAdjustmentPDF(invoiceType) && (
                  <div style={styles.pageCountWrapper.bankingInformation}>
                    <div>{payee?.name}</div>
                    <div>{payee?.bankName}</div>
                    <div>{`Routing: ${payee?.bankRoutingNumber}`}</div>
                    <br />
                    <div>{`Account: ${payee?.bankAccountNumber}`}</div>
                    <div>{`SWIFT: ${payee?.bankSwiftCode}`}</div>
                  </div>
                )}
                <div style={styles.pageCountWrapper.pageCount}>
                  {currentPageNumber} of {totalPagesComputed}
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
};
